import { useMemo, useEffect, useState, useCallback } from "react";
import { v4 as uuid } from "uuid";
import type { NodeData, EdgeData } from "../lib/api";
import { getContactCounts } from "../lib/contacts";

type Params = {
  nodeTemplates: { id: string; label: string; defaultColor?: string; defaultTextColor?: string }[];
  nodeTypeById: (id: string) => any;
  selectedNodeIds: string[];
  selectedEdgeIds: string[];
  rfNodes: any[];
  rfEdges: any[];
  resolveContacts: (node: NodeData) => any[];
  viewOnlyMode: boolean;
};

export function useInspectorState({ nodeTemplates, nodeTypeById, selectedNodeIds, selectedEdgeIds, rfNodes, rfEdges, resolveContacts, viewOnlyMode }: Params) {
  const [draftNode, setDraftNode] = useState<NodeData | null>(null);
  const [draftDirty, setDraftDirtyState] = useState(false);
  const [inspectorTab, setInspectorTab] = useState<"general" | "contacts" | "links" | "notes">("general");
  const [bulkNodeType, setBulkNodeType] = useState("");
  const [bulkNodeColor, setBulkNodeColor] = useState("#2563eb");
  const [bulkTextColor, setBulkTextColor] = useState("#ffffff");
  const [bulkEdgeColor, setBulkEdgeColor] = useState("#64748b");

  const primarySelectedNodeId = selectedNodeIds[0] || null;
  const primarySelectedEdgeId = selectedEdgeIds[0] || null;
  const selectedNode = useMemo(() => rfNodes.find((n) => n.id === primarySelectedNodeId), [rfNodes, primarySelectedNodeId]);
  const selectedEdge = useMemo(() => rfEdges.find((e) => e.id === primarySelectedEdgeId), [rfEdges, primarySelectedEdgeId]);
  const currentNodeData: NodeData | null = draftNode || (selectedNode?.data as NodeData | undefined) || null;

  useEffect(() => {
    if (selectedNode) {
      const raw = selectedNode.data as NodeData;
      const ensureContacts = (raw.contacts || []).map((c) => ({ id: c.id || uuid(), ...c }));
      const ensureLinks = (raw.links || []).map((l) => ({ id: l.id || uuid(), ...l }));
      const ensureNotes = (raw.notes || []).map((n) => ({ id: n.id || uuid(), ...n }));
      setDraftNode({ ...raw, contacts: ensureContacts, links: ensureLinks, notes: ensureNotes, contact_roles: raw.contact_roles || {} });
      setDraftDirtyState(false);
    } else {
      setDraftNode(null);
      setDraftDirtyState(false);
    }
  }, [selectedNode]);

  useEffect(() => {
    setInspectorTab("general");
  }, [primarySelectedEdgeId, primarySelectedNodeId]);

  const selectedNodes = useMemo(() => rfNodes.filter((n) => selectedNodeIds.includes(n.id)), [rfNodes, selectedNodeIds]);
  const selectedEdges = useMemo(() => rfEdges.filter((e) => selectedEdgeIds.includes(e.id)), [rfEdges, selectedEdgeIds]);

  const hasSelection = selectedNodes.length > 0 || selectedEdges.length > 0;
  const isBulk = selectedNodes.length + selectedEdges.length >= 2;

  return {
    draftNode,
    setDraftNode,
    draftDirty,
    setDraftDirty: setDraftDirtyState,
    inspectorTab,
    setInspectorTab,
    bulkNodeType,
    setBulkNodeType,
    bulkNodeColor,
    setBulkNodeColor,
    bulkTextColor,
    setBulkTextColor,
    bulkEdgeColor,
    setBulkEdgeColor,
    primarySelectedNodeId,
    primarySelectedEdgeId,
    selectedNode,
    selectedEdge,
    currentNodeData,
    selectedNodes,
    selectedEdges,
    hasSelection,
    isBulk,
  };
}
